'use client';
import React, { useEffect, useRef, useState } from 'react';

const stages = [
{
  number: '01',
  badge: 'Stage 1',
  title: 'Initial Rinse',
  description: 'A thorough pre-rinse blasts off loose dirt, mud, and surface debris before any contact is made with the paint. This critical first step prevents dragging contaminants across the finish.',
  detail: 'High-pressure rinse · Wheel wells · Door jambs · Undercarriage flush',
  metric: { label: 'Loose Debris Removed', value: '1st', sub: 'before any paint contact' },
},
{
  number: '02',
  badge: 'Stage 2',
  title: 'Foam Wash',
  description: 'A thick layer of foam is applied to dwell and loosen bonded grime. The two-bucket wash mitt method is then used — one bucket of clean soapy water, one rinse bucket — ensuring the mitt is always clean before touching the paint. No swirl-inducing shortcuts.',
  detail: 'Foam cannon pre-soak · Two-bucket method · Dedicated wash mitts · Panel-by-panel technique',
  metric: { label: 'Swirl Risk', value: '0%', sub: 'two-bucket method eliminates cross-contamination' },
},
{
  number: '03',
  badge: 'Stage 3',
  title: 'Post-Wash Rinse',
  description: 'A full rinse removes all soap residue and any remaining loosened contaminants. Starting from the roof down, every panel is thoroughly flushed to ensure a clean, residue-free surface before drying.',
  detail: 'Top-down rinse · Soap residue removal · Panel flush · Final contaminant rinse',
  metric: { label: 'Soap Residue', value: '0%', sub: 'fully flushed before drying' },
},
{
  number: '04',
  badge: 'Stage 4',
  title: 'Hand Dry',
  description: 'Premium microfiber drying towels are used to gently blot and wipe the entire vehicle dry. Proper drying prevents water spots and mineral deposits from forming on the paint surface.',
  detail: 'Microfiber drying towels · Blot technique · Door jambs & mirrors · Water spot prevention',
  metric: { label: 'Water Spots', value: '0', sub: 'microfiber blot technique' },
},
{
  number: '05',
  badge: 'Stage 5',
  title: 'Tire Shine',
  description: 'A professional-grade tire dressing is applied to all four tires for a clean, finished look. The dressing conditions the rubber and leaves a rich, even sheen that completes the detail.',
  detail: 'Tire dressing applied · All four tires · Rubber conditioning · Even sheen finish',
  metric: { label: 'Finish', value: '100%', sub: 'tires dressed & conditioned' },
},
{
  number: '06',
  badge: 'Stage 6',
  title: 'Apply Finishing Touches',
  description: 'Every inch of the vehicle is inspected before it goes back to the customer. Windows are wiped, interior surfaces are checked, and any remaining touch-ups are completed. The vehicle is then staged and ready for a clean, confident handoff.',
  detail: 'Full walk-around inspection · Window wipe-down · Interior surface check · Final touch-ups · Vehicle staged for handoff',
  metric: { label: 'Ready for Handoff', value: '✓', sub: 'verified & customer-ready' },
},
];


export default function RevealStagesSection() {
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const observers = stageRefs.current.map((ref, i) => {
      if (!ref) return null;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveStage(i);
              entry.target.querySelectorAll('.reveal').forEach((el, idx) => {
                setTimeout(() => el.classList.add('visible'), idx * 120);
              });
            }
          });
        },
        { threshold: 0.35 }
      );
      obs.observe(ref);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <section id="reveal-stages" className="py-24 md:py-32" style={{ background: 'var(--cockpit-black)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-20">
          <div className="metric-pill mb-5 reveal">The Detail Process</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(32px, 5vw, 60px)', letterSpacing: '-0.03em', lineHeight: 1 }}>
            The Detail<br />
            <span style={{ color: 'var(--ceramic-blue)' }}>Process.</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto reveal reveal-delay-2" style={{ color: 'var(--aluminum-dim)', fontSize: '17px', lineHeight: 1.6 }}>
            Each stage below mirrors what happens to your paint — a meticulous process from start to finish.
          </p>
        </div>

        {/* Stage progress */}
        <div className="flex items-center justify-center gap-3 mb-20 reveal">
          {stages.map((s, i) =>
          <React.Fragment key={s.number}>
              <div className={`stage-dot ${i <= activeStage ? 'active' : ''}`} />
              {i < stages.length - 1 &&
            <div
              className="h-px w-12 md:w-20 transition-all duration-700"
              style={{ background: i < activeStage ? 'var(--ceramic-blue)' : 'var(--aluminum-faint)' }} />

            }
            </React.Fragment>
          )}
        </div>

        {/* Stages */}
        <div className="space-y-32">
          {stages.map((stage, i) =>
          <div
            key={stage.number}
            ref={(el) => {stageRefs.current[i] = el;}}
            className="max-w-2xl mx-auto">
            
              {/* Text side */}
              <div>
                <div className="stage-badge reveal">{stage.badge}</div>
                <div
                className="font-manrope font-extrabold reveal reveal-delay-1 mt-3 mb-2"
                style={{ fontSize: 'clamp(52px, 8vw, 100px)', color: 'rgba(255,255,255,0.04)', letterSpacing: '-0.05em', lineHeight: 1, userSelect: 'none' }}>
                  {stage.number}
                </div>
                <h3
                className="font-manrope font-extrabold text-white reveal reveal-delay-1 -mt-6"
                style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                  {stage.title}
                </h3>
                <p
                className="mt-5 reveal reveal-delay-2 leading-relaxed"
                style={{ color: 'var(--aluminum-dim)', fontSize: '16px' }}>
                  {stage.description}
                </p>
                <div
                className="mt-6 reveal reveal-delay-3 rounded-xl px-5 py-4 flex items-start gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(63,63,70,0.6)' }}>
                  <div
                  className="shrink-0 w-1 rounded-full self-stretch"
                  style={{ background: 'var(--ceramic-blue)', minHeight: '32px' }} />
                  <span style={{ fontSize: '13px', color: 'var(--aluminum-dim)', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
                    {stage.detail}
                  </span>
                </div>
                {/* Metric */}
                <div
                className="mt-8 reveal reveal-delay-4 inline-flex items-end gap-3 rounded-2xl px-6 py-5"
                style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <div>
                    <div
                    className="font-manrope font-extrabold text-white"
                    style={{ fontSize: '36px', letterSpacing: '-0.04em', lineHeight: 1 }}>
                      {stage.metric.value}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--ceramic-blue)', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px' }}>
                      {stage.metric.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--aluminum-dim)', marginTop: '4px' }}>
                      {stage.metric.sub}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}